import { logger } from '../services/logger.ts';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../lib/auth.tsx';
import { supabase } from '../lib/db.ts';

// Define interface for notifications
interface Notification {
  id: string;
  title: string;
  message: string;
  link: string | null;
  is_read: boolean;
  created_at: string;
  event_type: string;
}

export function UserNotificationDropdown() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Function to fetch notifications
  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Fetch the latest 20 notifications for the user
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
        
      if (error) {
        throw error;
      }
      
      // Update notifications state
      setNotifications(data || []);
      
      // Calculate unread count
      const unread = data?.filter(n => !n.is_read).length || 0;
      setUnreadCount(unread);
      
    } catch (error) {
      logger.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Mark a notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      // Update in Supabase
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
        
      if (error) {
        throw error;
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      
    } catch (error) {
      logger.error('Error marking notification as read:', error);
    }
  };
  
  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user || notifications.length === 0) return;
    
    try {
      // Get IDs of unread notifications
      const unreadIds = notifications
        .filter(n => !n.is_read)
        .map(n => n.id);
        
      if (unreadIds.length === 0) return;
      
      // Update in Supabase
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .in('id', unreadIds);
        
      if (error) {
        throw error;
      }
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true }))
      );
      
      // Reset unread count
      setUnreadCount(0);
      
    } catch (error) {
      logger.error('Error marking all notifications as read:', error);
    }
  };
  
  // Setup click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user]);
  
  // Set up real-time subscription for new notifications
  useEffect(() => {
    if (!user) return;
    
    console.log('Setting up real-time subscription for user:', user.id);
    
    // Subscribe to inserts on the notifications table for this user
    const subscription = supabase
      .channel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('✅ New notification received:', payload.new);
          // Add the new notification to the state
          const newNotification = payload.new as Notification;
          setNotifications(prev => [newNotification, ...prev].slice(0, 20));
          
          // Increment unread count only if the notification is unread
          if (!newNotification.is_read) {
            setUnreadCount(prev => {
              const newCount = prev + 1;
              console.log('📊 Unread count updated:', newCount);
              return newCount;
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('📝 Notification updated:', payload.new);
          // Update the notification in the state
          const updatedNotification = payload.new as Notification;
          setNotifications(prev => 
            prev.map(n => 
              n.id === updatedNotification.id ? updatedNotification : n
            )
          );
          
          // Recalculate unread count based on all notifications
          setNotifications(prevNotifications => {
            const updatedList = prevNotifications.map(n => 
              n.id === updatedNotification.id ? updatedNotification : n
            );
            const unread = updatedList.filter(n => !n.is_read).length;
            setUnreadCount(unread);
            console.log('📊 Unread count recalculated:', unread);
            return updatedList;
          });
        }
      )
      .subscribe((status) => {
        console.log('🔌 Realtime subscription status:', status);
      });
    
    // Cleanup subscription
    return () => {
      console.log('🧹 Cleaning up realtime subscription');
      supabase.removeChannel(subscription);
    };
  }, [user]);
  
  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };
  
  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }
  };
  
  // Handle notification click (mark as read + follow link if available)
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    // Close dropdown
    setIsOpen(false);
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell icon with badge */}
      <button
        type="button"
        className="relative p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wayja-teal"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={isOpen ? 'Close notifications' : 'Open notifications'}
      >
        <Bell className="h-6 w-6 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-wayja-teal rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      
      {/* Dropdown/Popover */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white shadow-lg rounded-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="p-3 border-b flex justify-between items-center">
            <h3 className="font-medium">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-wayja-teal hover:text-opacity-80"
              >
                Mark all as read
              </button>
            )}
          </div>
          
          {/* Body */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center">
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-wayja-teal"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                You have no notifications
              </div>
            ) : (
              <ul>
                {notifications.map(notification => (
                  <li
                    key={notification.id}
                    className={`border-b last:border-b-0 ${!notification.is_read ? 'bg-blue-50' : ''}`}
                  >
                    {notification.link ? (
                      <Link
                        to={notification.link}
                        className="block p-3 hover:bg-gray-50"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex justify-between">
                          <div className="font-medium text-gray-900">{notification.title}</div>
                          <div className="text-xs text-gray-500">
                            {formatRelativeTime(notification.created_at)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{notification.message}</p>
                      </Link>
                    ) : (
                      <div
                        className="block p-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex justify-between">
                          <div className="font-medium text-gray-900">{notification.title}</div>
                          <div className="text-xs text-gray-500">
                            {formatRelativeTime(notification.created_at)}
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">{notification.message}</p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {/* Footer */}
          <div className="p-3 border-t text-center">
            <Link
              to="/settings/notifications"
              className="text-sm text-wayja-teal hover:text-opacity-80"
              onClick={() => setIsOpen(false)}
            >
              Notification Settings
            </Link>
          </div>
        </div>
      )}
    </div>
  );
} 